// src/components/social/PostComposer.jsx
import React, { useRef, useState } from "react";
import apiClient from "../../Api/apiConfig";
import { useAuth } from "../../lib/auth";
import "../../styles/social.css";

export default function PostComposer({ onPostCreated }) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef(null);

  // Petit set d’emojis pour faire simple
  const emojis = ["😀", "😂", "😍", "👏", "🔥", "🙏", "👍", "😢"];

  const handleChoosePhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);

    // Preview locale
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const handleAddEmoji = (emoji) => {
    setContent((prev) => prev + emoji);
    setShowEmojiPicker(false);
  };

  const resetForm = () => {
    setContent("");
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() && !imageFile) return;

    setIsSubmitting(true);

    try {
      let mediaUrl = null;

      // 1️⃣ Si une image est choisie, on l’upload d’abord
      if (imageFile) {
        const formData = new FormData();
        formData.append("image", imageFile);

        const uploadRes = await apiClient.post(
          "/social/upload-image",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        // L’API renvoie { url: fullUrl, path: relativePath }
        // 👉 On stocke le CHEMIN RELATIF en base
        mediaUrl = uploadRes.data?.path || uploadRes.data?.url || null;
      }

      // 2️⃣ On crée le post avec le contenu + le chemin de l’image
      const postRes = await apiClient.post("/social/posts", {
        content: content.trim(),
        mediaUrl, // le controller se charge de l’enregistrer en BDD
      });

      const newPost = postRes.data;

      // 3️⃣ On informe le parent pour l’ajouter en tête du feed
      if (onPostCreated) {
        onPostCreated(newPost);
      }

      // 4️⃣ On nettoie le formulaire
      resetForm();
    } catch (error) {
      console.error("Erreur création post :", error);
      alert("Impossible de publier le post pour le moment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const userInitial = user?.firstName?.[0]?.toUpperCase() || "N";

  return (
    <div className="composer-card">
      <div className="composer-header">
        <div className="avatar">{userInitial}</div>
        <div className="composer-info">
          <div className="composer-name">
            {user?.firstName} {user?.lastName}
          </div>
          <div className="composer-placeholder">Exprimez-vous…</div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <textarea
          className="composer-textarea"
          placeholder="Quoi de neuf ?"
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />

        {/* Preview image si présente */}
        {imagePreview && (
          <div className="composer-image-preview">
            <img src={imagePreview} alt="Prévisualisation" />
            <button
              type="button"
              className="composer-image-remove"
              onClick={() => {
                setImageFile(null);
                setImagePreview(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Input fichier caché */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <div className="composer-footer">
          <div className="composer-actions">
            <button
              type="button"
              className="composer-action-button"
              onClick={handleChoosePhoto}
            >
              📷 Photo
            </button>

            <div className="composer-emoji-wrapper">
              <button
                type="button"
                className="composer-action-button"
                onClick={() => setShowEmojiPicker((s) => !s)}
            >
                😊 Emoji
              </button>

              {showEmojiPicker && (
                <div className="emoji-picker">
                  {emojis.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => handleAddEmoji(e)}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="composer-submit-button"
            disabled={isSubmitting || (!content.trim() && !imageFile)}
          >
            {isSubmitting ? "Publication…" : "Publier"}
          </button>
        </div>
      </form>
    </div>
  );
}
